// Library imports
import React, { useState, useEffect } from 'react';
import { MDBIcon } from 'mdb-react-ui-kit';
import Skeleton from 'react-loading-skeleton';
import { useDispatch, useSelector } from 'react-redux';
// Component imports
import NoData from '../../../../../components/NoData';
import TwoColumnView from '../../../../../components/TwoColumnView';
import TabNav from '../../../../../components/TabNav';
// Styled components
import { CardTitle, InnerContent, TeamLogoSmall } from '../../styles';
import { PlayerItem, PlayerListGroup, PositionHeader, TeamCard } from './styles';
// API imports
import { getRosterByTeam } from '../../../../../api/games';
import { getProps } from '../../../../../api/props';
// Util imports
import { imageLoadError } from '../../../../../utils';
import ExampleRosterData from '../../../../../utils/mock_data/ExampleRosterData';
import { getGameTeamData } from '../../../utils';
// Action imports
import { setDrawer } from '../../../../../store/slices/appSlice';
import { initPropFinderWithGame } from '../../../../../store/slices/aiSlice';
// Service imports
import { logEvent } from '../../../../../services/Firebase/analyticsHelper';

const TeamRoster = ({ game, setView, setPlayer, sport, demo }) => {
    // Redux
    const dispatch = useDispatch();
    const { teams } = useSelector(state => state.app);
    // Local state
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState('full roster');
    const [propsLoading, setPropsLoading] = useState(false);
    const [awayRoster, setAwayRoster] = useState(null);
    const [homeRoster, setHomeRoster] = useState(null);
    const [awayPlayersWithProps, setAwayPlayersWithProps] = useState([]);
    const [homePlayersWithProps, setHomePlayersWithProps] = useState([]);
    const [propsFetched, setPropsFetched] = useState(false);

    const handlePropFinder = () => {
        if (demo) {
            return;
        }
        logEvent('prop_finder_from_roster_web');
        dispatch(initPropFinderWithGame(game));
        dispatch(setDrawer('prophetAI'));
    };

    useEffect(() => {
        async function fetchRostersFromTeamsState() {
            try {
                setLoading(true);
                const { awayTeamData, homeTeamData } = getGameTeamData(teams, game, demo, sport);
                const awayRes = demo ? ExampleRosterData[sport].awayTeam : awayTeamData;
                const homeRes = demo ? ExampleRosterData[sport].homeTeam : homeTeamData;
                const normalizeRoster = (apiRoster, gameTeam) => {
                    if (apiRoster?.players?.length) return apiRoster;
                    if (apiRoster?.roster?.players?.length) {
                        return { ...apiRoster, players: apiRoster.roster.players };
                    }
                    if (gameTeam?.players?.length) return { ...gameTeam, players: gameTeam.players };
                    if (gameTeam?.roster?.players?.length) return { ...gameTeam, players: gameTeam.roster.players };
                    return apiRoster || gameTeam || null;
                };

                let away = normalizeRoster(awayRes, game.awayTeam);
                let home = normalizeRoster(homeRes, game.homeTeam);

                // If teams state has no players, fetch directly from LPA public roster endpoint
                if (!away?.players?.length && game.awayTeam?.teamId) {
                    try {
                        const apiAway = await getRosterByTeam(sport, game.awayTeam.teamId);
                        if (apiAway?.players?.length) away = apiAway;
                    } catch (_) { /* silent – fall through */ }
                }
                if (!home?.players?.length && game.homeTeam?.teamId) {
                    try {
                        const apiHome = await getRosterByTeam(sport, game.homeTeam.teamId);
                        if (apiHome?.players?.length) home = apiHome;
                    } catch (_) { /* silent – fall through */ }
                }

                setAwayRoster(away);
                setHomeRoster(home);
            } catch (error) {
                console.log(`Error fetching rosters: ${error.message}`);
                // Hard fallback to team payload included in game data.
                const awayFallback = game.awayTeam?.players?.length
                    ? { ...game.awayTeam, players: game.awayTeam.players }
                    : game.awayTeam?.roster?.players?.length
                      ? { ...game.awayTeam, players: game.awayTeam.roster.players }
                      : null;
                const homeFallback = game.homeTeam?.players?.length
                    ? { ...game.homeTeam, players: game.homeTeam.players }
                    : game.homeTeam?.roster?.players?.length
                      ? { ...game.homeTeam, players: game.homeTeam.roster.players }
                      : null;
                setAwayRoster(awayFallback);
                setHomeRoster(homeFallback);
            } finally {
                setLoading(false);
            }
        }
        fetchRostersFromTeamsState();
    }, [teams, game, demo, sport]);

    useEffect(() => {
        async function fetchPlayersWithProps() {
            try {
                setPropsLoading(true);
                const props = await getProps(sport);
                const propList = Array.isArray(props) ? props : Array.isArray(props?.data) ? props.data : [];
                const validTeamIds = new Set([String(game.homeTeam.teamId), String(game.awayTeam.teamId)]);

                const rosterPlayers = [
                    ...(Array.isArray(awayRoster?.players) ? awayRoster.players : []),
                    ...(Array.isArray(homeRoster?.players) ? homeRoster.players : []),
                ];

                const propsPlayersByKey = new Map();
                propList.forEach(prop => {
                    const teamId = prop?.teamId ?? prop?.team?.teamId ?? prop?.player?.teamId;
                    if (!validTeamIds.has(String(teamId))) return;

                    const playerId = prop?.playerId ?? prop?.player?.playerId ?? prop?.player?.id;
                    const playerName =
                        prop?.playerName ??
                        prop?.player?.name ??
                        (prop?.player?.firstName && prop?.player?.lastName ? `${prop.player.firstName} ${prop.player.lastName}` : null);
                    if (!playerId && !playerName) return;

                    const key = `${String(teamId)}:${String(playerId || playerName).toLowerCase()}`;
                    if (!propsPlayersByKey.has(key)) {
                        const rosterMatch = rosterPlayers.find(player => {
                            if (playerId && player?.playerId) return String(player.playerId) === String(playerId);
                            return player?.name && playerName && String(player.name).toLowerCase() === String(playerName).toLowerCase();
                        });

                        propsPlayersByKey.set(key, {
                            id: rosterMatch?.playerId || playerId || null,
                            playerId: rosterMatch?.playerId || playerId || null,
                            name: rosterMatch?.name || playerName || 'Unknown Player',
                            position: rosterMatch?.position || prop?.position || prop?.player?.position || 'N/A',
                            teamId: Number(rosterMatch?.teamId || teamId),
                        });
                    }
                });

                const players = Array.from(propsPlayersByKey.values());
                setAwayPlayersWithProps(players.filter(p => p.teamId === Number(game.awayTeam.teamId)));
                setHomePlayersWithProps(players.filter(p => p.teamId === Number(game.homeTeam.teamId)));
                setPropsFetched(true);
            } catch (error) {
                console.log(`Error fetching players with props: ${error.message}`);
                setPropsFetched(true);
            } finally {
                setPropsLoading(false);
            }
        }
        if (activeTab.toLowerCase() === 'players with props' && !propsFetched) {
            fetchPlayersWithProps();
        }
    }, [activeTab, sport, game, awayRoster, homeRoster]);

    const allPositions = React.useMemo(() => {
        if (activeTab === 'players with props') {
            const awayPropsPositions = awayPlayersWithProps?.map(p => p.position) || [];
            const homePropsPositions = homePlayersWithProps?.map(p => p.position) || [];
            const unique = Array.from(new Set([...awayPropsPositions, ...homePropsPositions]));
            return unique.sort((a, b) => a.localeCompare(b));
        }
        const awayPositions = awayRoster?.players?.map(p => p.position) || [];
        const homePositions = homeRoster?.players?.map(p => p.position) || [];
        const unique = Array.from(new Set([...awayPositions, ...homePositions]));
        return unique.sort((a, b) => a.localeCompare(b));
    }, [awayRoster, homeRoster, activeTab, awayPlayersWithProps, homePlayersWithProps]);

    const groupByPosition = players => {
        if (!players?.length) return {};
        return players.reduce((acc, player) => {
            const pos = player.position || 'Unknown';
            if (!acc[pos]) acc[pos] = [];
            acc[pos].push(player);
            return acc;
        }, {});
    };

    const teamHeader = (team) => (
        <CardTitle style={{ padding: '0.65rem 0.85rem 0.5rem' }}>
            {team?.logoUrl && <TeamLogoSmall src={team.logoUrl} alt="" onError={imageLoadError} />}
            {loading ? <Skeleton width={80} /> : team?.teamName || 'Unknown Team'}
        </CardTitle>
    );

    const createRosterJSX = (roster, playersWithProps) => {
        if (!roster) return null;
        const team = roster.teamId === game.awayTeam.teamId ? game.awayTeam : game.homeTeam;
        const grouped = groupByPosition(activeTab === 'players with props' ? playersWithProps : roster?.players);

        if (activeTab === 'players with props' && allPositions.length === 0) {
            return (
                <TeamCard>
                    {teamHeader(team)}
                    <NoData title="No players with props" message="There are currently no players with available props for this team." />
                </TeamCard>
            );
        }
        return (
            <TeamCard>
                {teamHeader(team)}
                {!roster?.players?.length ? (
                    <NoData title="No players" message="There are currently no players for this team." />
                ) : (
                    <PlayerListGroup>
                        {allPositions.map((position, idx) => {
                            const hasPlayers = grouped[position]?.length > 0;
                            if (activeTab === 'players with props' && !hasPlayers) return null;
                            return (
                                <React.Fragment key={`${position}-${idx}`}>
                                    <PositionHeader>{position}</PositionHeader>
                                    {hasPlayers ? (
                                        grouped[position].map((player, i) => (
                                            <PlayerItem
                                                key={`${player.id}-${i}`}
                                                onClick={() => { setPlayer(player); setView('player-data'); }}
                                            >
                                                <span className="player-name">{player.name}</span>
                                                <MDBIcon fas icon="angle-right" className="chevron" />
                                            </PlayerItem>
                                        ))
                                    ) : (
                                        <div style={{ padding: '0.5rem 0.85rem', color: '#94a3b8', fontSize: '0.78rem', textAlign: 'center' }}>
                                            — No {position} —
                                        </div>
                                    )}
                                </React.Fragment>
                            );
                        })}
                    </PlayerListGroup>
                )}
            </TeamCard>
        );
    };

    const skeletonCard = (team) => (
        <TeamCard>
            {teamHeader(team)}
            <div style={{ padding: '0.5rem 0.85rem' }}><Skeleton count={4} baseColor="#1e2533" highlightColor="#2a3347" /></div>
        </TeamCard>
    );

    return (
        <InnerContent fluid>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.75rem' }}>
                <TabNav
                    tabs={demo ? ['full roster', 'players with props'] : ['players with props', 'full roster']}
                    activeTab={activeTab}
                    setTab={setActiveTab}
                    containerStyle={{ marginBottom: 0, flex: 1 }}
                    disabled={demo}
                />
                <button
                    onClick={handlePropFinder}
                    disabled={demo}
                    style={{
                        marginLeft: '0.65rem',
                        whiteSpace: 'nowrap',
                        borderRadius: '999px',
                        padding: '0.38rem 0.75rem',
                        fontWeight: 600,
                        fontSize: '0.72rem',
                        letterSpacing: '0.03em',
                        background: 'rgba(59,130,246,0.12)',
                        border: '1px solid rgba(59,130,246,0.4)',
                        color: '#93c5fd',
                        cursor: demo ? 'not-allowed' : 'pointer',
                        opacity: demo ? 0.5 : 1,
                    }}
                >
                    <MDBIcon fas icon="search-dollar" style={{ marginRight: 5 }} />
                    Prop Finder
                </button>
            </div>
            {loading || (activeTab === 'players with props' && propsLoading) ? (
                <TwoColumnView
                    columnOneJSX={skeletonCard(game.awayTeam)}
                    columnTwoJSX={skeletonCard(game.homeTeam)}
                />
            ) : !awayRoster && !homeRoster ? (
                <NoData title="No roster data" message="There are currently no roster data available for this game." />
            ) : (
                <TwoColumnView
                    columnOneJSX={createRosterJSX(awayRoster, awayPlayersWithProps)}
                    columnTwoJSX={createRosterJSX(homeRoster, homePlayersWithProps)}
                />
            )}
        </InnerContent>
    );
};

export default TeamRoster;
